import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function Auth() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/account";
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [showRegPw, setShowRegPw] = useState(false);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success("Bentornato!");
      navigate(redirect);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(regForm.name, regForm.email, regForm.password);
      toast.success("Account creato. Benvenuto in CatenaMotosega.net!");
      navigate(redirect);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-page" className="max-w-md mx-auto px-4 sm:px-6 py-16">
      <div className="overline-tag text-center">Area clienti</div>
      <h1 className="mt-3 font-display font-medium uppercase tracking-tight text-4xl text-center">Accedi o registrati</h1>

      <Tabs defaultValue="login" className="mt-10">
        <TabsList className="grid grid-cols-2 w-full rounded-none">
          <TabsTrigger data-testid="tab-login" value="login" className="rounded-none uppercase text-xs font-bold tracking-wide">Accedi</TabsTrigger>
          <TabsTrigger data-testid="tab-register" value="register" className="rounded-none uppercase text-xs font-bold tracking-wide">Registrati</TabsTrigger>
        </TabsList>

        <TabsContent value="login">
          <form onSubmit={doLogin} className="mt-6 bg-white border border-[#e5e5e5] p-6 flex flex-col gap-4">
            <Input
              data-testid="login-email-input"
              type="email"
              required
              value={loginForm.email}
              onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
              placeholder="Email"
              className="rounded-none border-2 border-[#0a0a0a]"
            />
            <div className="relative">
              <Input
                data-testid="login-password-input"
                type={showLoginPw ? "text" : "password"}
                required
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                placeholder="Password"
                className="rounded-none border-2 border-[#0a0a0a] pr-11"
              />
              <button
                type="button"
                data-testid="login-password-toggle"
                onClick={() => setShowLoginPw(!showLoginPw)}
                aria-label={showLoginPw ? "Nascondi password" : "Mostra password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#ff4500] transition-colors"
              >
                {showLoginPw ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
              </button>
            </div>
            <button data-testid="login-submit-button" disabled={loading} className="btn-hard w-full">
              {loading ? "Accesso..." : "Accedi"}
            </button>
          </form>
        </TabsContent>

        <TabsContent value="register">
          <form onSubmit={doRegister} className="mt-6 bg-white border border-[#e5e5e5] p-6 flex flex-col gap-4">
            <Input
              data-testid="register-name-input"
              required
              value={regForm.name}
              onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
              placeholder="Nome e cognome"
              className="rounded-none border-2 border-[#0a0a0a]"
            />
            <Input
              data-testid="register-email-input"
              type="email"
              required
              value={regForm.email}
              onChange={(e) => setRegForm({ ...regForm, email: e.target.value })}
              placeholder="Email"
              className="rounded-none border-2 border-[#0a0a0a]"
            />
            <div className="relative">
              <Input
                data-testid="register-password-input"
                type={showRegPw ? "text" : "password"}
                required
                minLength={6}
                value={regForm.password}
                onChange={(e) => setRegForm({ ...regForm, password: e.target.value })}
                placeholder="Password (min. 6 caratteri)"
                className="rounded-none border-2 border-[#0a0a0a] pr-11"
              />
              <button
                type="button"
                data-testid="register-password-toggle"
                onClick={() => setShowRegPw(!showRegPw)}
                aria-label={showRegPw ? "Nascondi password" : "Mostra password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#ff4500] transition-colors"
              >
                {showRegPw ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
              </button>
            </div>
            <button data-testid="register-submit-button" disabled={loading} className="btn-dark w-full">
              {loading ? "Creazione..." : "Crea account"}
            </button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}
